import { fetchWithAuth } from "./fetch";

/** PushManager wants the VAPID public key as a raw Uint8Array, not the base64url string the backend returns. */
export function urlBase64ToUint8Array(base64url: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

async function registerWithBackend(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  await fetchWithAuth(`${process.env.API}/push/subscribe`, {
    method: "POST",
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: json.keys,
      user_agent: navigator.userAgent,
    }),
  });
}

/**
 * Must be called from a user gesture handler (button onClick) — `Notification.requestPermission()`
 * is ignored or auto-rejected by some browsers otherwise.
 */
export async function subscribeToPush(): Promise<void> {
  if (!pushSupported()) throw new Error("Push notifications are not supported in this browser");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error(`Notification permission was ${permission}`);

  const registration = await navigator.serviceWorker.ready;
  const keyResponse = await fetch(`${process.env.API}/push/vapid-public-key`);
  if (!keyResponse.ok) throw new Error("Could not load the VAPID public key");
  const { public_key: publicKey } = (await keyResponse.json()) as { public_key: string };

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  await registerWithBackend(subscription);
}

/**
 * `/push/subscribe` is an upsert-by-endpoint that also clears a server-side `revoked_at` (e.g. after
 * a delivery failure marked the subscription dead) — so re-registering the browser's EXISTING
 * subscription, without asking for permission again, heals that silently. Returns false when the
 * browser side itself has nothing to re-register (permission was revoked, or there never was one) or
 * the re-registration call itself failed — either way surfaced by the caller as a "stale" state.
 */
export async function refreshPushSubscription(): Promise<boolean> {
  if (!pushSupported() || Notification.permission !== "granted") return false;
  const subscription = await currentPushSubscription();
  if (!subscription) return false;
  try {
    await registerWithBackend(subscription);
    return true;
  } catch {
    return false;
  }
}

export async function unsubscribeFromPush(): Promise<void> {
  if (!pushSupported()) return;

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  await fetchWithAuth(`${process.env.API}/push/unsubscribe`, {
    method: "POST",
    body: JSON.stringify({ endpoint }),
  });
}

/** Read-only: does this browser already have a push subscription? For rendering button state. */
export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}
